"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import FAIcon from "@/components/icons/faIcon";
import { pushLeadConversion } from "@/lib/analytics";
import { getAttributionForSubmission } from "@/lib/attribution";
import { isValidEmail } from "@/lib/misc";

const Recaptcha = dynamic(() => import("@/components/common/RecaptchaClient"), {
  ssr: false,
});

const initialFields = {
  firstName: "",
  lastName: "",
  email: "",
};

const recaptchaVerifyTimeoutMs = 25000;
const contactRequestTimeoutMs = 30000;

const inputClass =
  "w-full border border-black/20 bg-white px-4 py-3.5 text-base text-[#06040c] outline-none transition-colors duration-200 placeholder:text-[#06040c]/45 focus:border-primary focus:ring-2 focus:ring-primary/15";

export default function AssessmentContactForm({
  sectionId,
  pageSource,
  title = "Get in touch",
  description,
  successTitle = "Enquiry received.",
  successDescription = "Thanks for sending your details. The team will review your enquiry and come back to you within one working day.",
}) {
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY?.trim();
  const [fields, setFields] = useState(initialFields);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const recaptchaRef = useRef(null);
  const requestRef = useRef(null);
  const verifyTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const isFormValid = Boolean(
    fields.firstName.trim() &&
      isValidEmail(fields.email.trim()),
  );
  const isSubmitting = status === "verifying" || status === "sending";

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      requestRef.current?.abort?.();
      requestRef.current = null;

      if (verifyTimeoutRef.current) {
        clearTimeout(verifyTimeoutRef.current);
        verifyTimeoutRef.current = null;
      }
    };
  }, []);

  const clearVerifyTimeout = () => {
    if (verifyTimeoutRef.current) {
      clearTimeout(verifyTimeoutRef.current);
      verifyTimeoutRef.current = null;
    }
  };

  const resetRecaptcha = () => {
    try {
      recaptchaRef.current?.reset?.();
    } catch {}
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (status === "error" || status === "config-error") {
      setStatus("idle");
      setErrorMessage("");
    }

    setFields((currentFields) => ({
      ...currentFields,
      [name]: value,
    }));
  };

  const resetForm = () => {
    clearVerifyTimeout();
    resetRecaptcha();
    setFields(initialFields);
    setErrorMessage("");
    setStatus("idle");
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!isFormValid) {
      return;
    }

    if (!recaptchaSiteKey || !recaptchaRef.current) {
      setStatus("config-error");
      return;
    }

    setStatus("verifying");
    setErrorMessage("");
    clearVerifyTimeout();
    verifyTimeoutRef.current = window.setTimeout(() => {
      if (!isMountedRef.current) {
        return;
      }

      setErrorMessage("reCAPTCHA verification timed out. Please try again.");
      setStatus((currentStatus) =>
        currentStatus === "verifying" ? "error" : currentStatus,
      );
      resetRecaptcha();
    }, recaptchaVerifyTimeoutMs);

    try {
      recaptchaRef.current.execute();
    } catch {
      clearVerifyTimeout();
      setErrorMessage("Unable to start reCAPTCHA verification.");
      setStatus("error");
      resetRecaptcha();
    }
  };

  const handleRecaptchaChange = async (token) => {
    clearVerifyTimeout();

    if (!token || !isMountedRef.current) {
      return;
    }

    setStatus("sending");
    setErrorMessage("");

    const requestController = new AbortController();
    const requestTimeout = window.setTimeout(
      () => requestController.abort(),
      contactRequestTimeoutMs,
    );
    requestRef.current = requestController;

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: fields.firstName.trim(),
          lastName: fields.lastName.trim(),
          email: fields.email.trim(),
          formType: "Website Enquiry",
          pageSource,
          recaptchaToken: token,
          attribution: getAttributionForSubmission(),
        }),
        signal: requestController.signal,
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          payload?.details
            ? `${payload?.message || "Request failed"} (${payload.details})`
            : payload?.message || "Request failed",
        );
      }

      if (!isMountedRef.current) {
        return;
      }

      pushLeadConversion({
        formId: sectionId,
        formName: "Assessment enquiry",
        formLocation: pageSource,
        leadId: payload?.leadReference || payload?.leadId || undefined,
        enhancedConversionData: {
          email: fields.email.trim(),
          first_name: fields.firstName.trim(),
          last_name: fields.lastName.trim(),
        },
      });

      setStatus("success");
      setFields(initialFields);
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      setErrorMessage(
        error?.name === "AbortError"
          ? "The enquiry request timed out. Please try again."
          : error?.message || "Something went wrong. Please try again.",
      );
      setStatus("error");
    } finally {
      window.clearTimeout(requestTimeout);

      if (requestRef.current === requestController) {
        requestRef.current = null;
      }

      if (isMountedRef.current) {
        resetRecaptcha();
      }
    }
  };

  const handleRecaptchaExpired = () => {
    clearVerifyTimeout();

    if (!isMountedRef.current) {
      return;
    }

    setErrorMessage("reCAPTCHA verification expired. Please try again.");
    setStatus((currentStatus) =>
      currentStatus === "verifying" ? "error" : currentStatus,
    );
    resetRecaptcha();
  };

  const handleRecaptchaError = () => {
    clearVerifyTimeout();

    if (!isMountedRef.current) {
      return;
    }

    setErrorMessage("reCAPTCHA verification failed. Please try again.");
    setStatus((currentStatus) =>
      currentStatus === "verifying" ? "error" : currentStatus,
    );
    resetRecaptcha();
  };

  return (
    <section id={sectionId} className="w-full scroll-mt-24 bg-[#f7f7f5] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
      <div className="mx-auto grid w-full max-w-328 gap-10 lg:grid-cols-[minmax(240px,0.72fr)_minmax(0,1.28fr)] lg:gap-20">
        <div className="space-y-5">
          <h2 className="font-raleway text-[2.65rem] font-medium leading-[0.95] tracking-[-0.06em] text-[#06040c] sm:text-[3.3rem]">
            {title}
          </h2>
          {description ? (
            <p className="max-w-md text-base leading-7 text-[#06040c]/65 sm:text-[1.05rem]">
              {description}
            </p>
          ) : null}
        </div>

        <div className="min-w-0 bg-white p-6 shadow-[0_16px_50px_rgba(6,4,12,0.08)] sm:p-8 lg:p-10">
          {status === "success" ? (
            <div className="flex min-h-72 flex-col items-center justify-center gap-5 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary">
                <FAIcon icon="check" size={20} noMargin className="text-[#06040c]" />
              </div>
              <h3 className="font-raleway text-[2rem] font-semibold leading-none tracking-[-0.05em] text-[#06040c]">
                {successTitle}
              </h3>
              <p className="max-w-md text-base leading-7 text-[#06040c]/65">
                {successDescription}
              </p>
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center gap-2 border-b border-[#06040c]/40 pb-1 text-sm font-semibold text-[#06040c]"
              >
                Send another enquiry
                <FAIcon icon="arrow-right" size={12} noMargin />
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="flex flex-col gap-3 text-sm font-medium text-[#06040c]">
                  <span>First Name</span>
                  <input
                    type="text"
                    name="firstName"
                    value={fields.firstName}
                    onChange={handleChange}
                    autoComplete="given-name"
                    placeholder="First Name"
                    className={inputClass}
                    required
                  />
                </label>

                <label className="flex flex-col gap-3 text-sm font-medium text-[#06040c]">
                  <span>Last Name</span>
                  <input
                    type="text"
                    name="lastName"
                    value={fields.lastName}
                    onChange={handleChange}
                    autoComplete="family-name"
                    placeholder="Last Name"
                    className={inputClass}
                  />
                </label>
              </div>

              <label className="flex flex-col gap-3 text-sm font-medium text-[#06040c]">
                <span>Business Email</span>
                <input
                  type="email"
                  name="email"
                  value={fields.email}
                  onChange={handleChange}
                  autoComplete="email"
                  inputMode="email"
                  placeholder="Business Email"
                  className={inputClass}
                  required
                />
              </label>

              <p className="text-sm leading-6 text-[#06040c]/60">
                By clicking submit, you agree to Safety Management Limited storing and processing your information to deliver the requested content.
              </p>

              {recaptchaSiteKey ? (
                <Recaptcha
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={recaptchaSiteKey}
                  onChange={handleRecaptchaChange}
                  onExpired={handleRecaptchaExpired}
                  onErrored={handleRecaptchaError}
                />
              ) : null}

              {status === "config-error" ? (
                <p className="border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  Contact form setup is incomplete. Please try again later.
                </p>
              ) : null}

              {status === "error" ? (
                <p className="border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-600">
                  {errorMessage || "Something went wrong. Please try again."}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={isSubmitting || !recaptchaSiteKey || !isFormValid}
                className="inline-flex h-14 w-full items-center justify-center gap-2 bg-[#06040c] px-8 text-base font-semibold text-white transition-colors duration-200 hover:bg-[#06040c]/90 disabled:cursor-not-allowed disabled:opacity-55"
              >
                {isSubmitting ? (
                  <>
                    <FAIcon icon="rotate" spin size={13} noMargin />
                    {status === "verifying" ? "Verifying" : "Sending"}
                  </>
                ) : (
                  <>
                    Send
                    <FAIcon icon="arrow-right" size={13} noMargin />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}